import { Component, Vue, Prop, Inject, Watch } from 'vue-property-decorator';
import { CreditCardTokenData, createCreditCardTokenData, PaymentProcessorEnum, GoogleAddressComponentNameMap, createGoogleAddressComponentNameMap, GoogleAddressComponentValueMap, createGoogleAddressComponentValueMap, AutoPayOption } from '@/common/types/AllTypesExports';
import QuickBooksPaymentsCardInput from './QuickBooksPaymentsCardInput.vue';
import { default as QuickBooksPaymentsCardInputClass } from './QuickBooksPaymentsCardInputVue';
import { default as FiservPaymentsCardInputClass } from './FiservPaymentsCardInputVue';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';
import FiservPaymentsCardInput from './FiservPaymentsCardInput.vue';

declare let noviNs: INoviNsPaymentGlobals;

@Component({
    name: 'CreditCardEntryForm',
    components: {
        QuickBooksPaymentsCardInput,
        FiservPaymentsCardInput,
        FieldValidationErrors
    }
})
export default class CreditCardEntryForm extends Vue {
    @Prop({ required: false, default: () => false })
    saveCardOption!: boolean;

    @Prop({ required: false, default: () => false })
    forceSaveCard!: boolean;

    @Prop({ required: false, default: () => true })
    validate!: boolean;

    @Prop({ required: false, default: () => false })
    locked!: boolean;

    creditCardData: CreditCardTokenData = createCreditCardTokenData();

    addressAutocomplete!: google.maps.places.Autocomplete;
    addressAutocompleteListener!: google.maps.MapsEventListener;
    mapsIsInitialized = false;

    // This object allows us to map the incoming google result keys
    addressComponentNamesMap: GoogleAddressComponentNameMap = createGoogleAddressComponentNameMap();

    // This object allows us to map the incoming google result values
    addressComponentValues: GoogleAddressComponentValueMap = createGoogleAddressComponentValueMap();

    @Inject('validator')
    $validator: any;

    @Watch('forceSaveCard', { immediate: true })
    onForceSaveCard(forceSave: boolean) {
        if (this.saveCardOption && forceSave)
            this.creditCardData.saveCard = true;
    }

    @Watch('creditCardData.billingState')
    onBillingStateChange(billingState: string) {
        this.$emit('update:credit-card-billing-state', billingState);
    }

    get isPaymentProcessorStripe() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.stripe || noviNs.paymentProcessor == PaymentProcessorEnum.noviPay;
    }

    get isPaymentProcessorQuickBookPayments() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.quickBooksPayments;
    }

    get isPaymentProcessorFiserv() {
        return noviNs.paymentProcessor == PaymentProcessorEnum.fiserv;
    }

    get showCountry() {
        return true;
    }

    onCreditCardPaymentTokenReady(token: CreditCardTokenData) {
        Object.assign(this.creditCardData, token);
        this.$emit('on-payment-token-ready', token);
    }


    onError(errorMsg: string) {
        this.emitError(errorMsg);
    }

    emitError(errorMsg: string) {
        this.$emit('on-error-toggle', true, errorMsg);
    }

    setupGoogleAutocomplete() {
        if (typeof google === 'undefined') {
            return;
        }

        const elemId = "billingStreet";
        const addressElem = document.getElementById(elemId);

        if (!addressElem) {
            return;
        }

        addressElem.addEventListener("focus", function () {
            addressElem.setAttribute("autocomplete", "nope");
        });

        this.addressAutocomplete = new google.maps.places.Autocomplete(<HTMLInputElement>addressElem, {
            types: ["geocode"],
            fields: ["address_components"],
        });
        this.addressAutocompleteListener = this.addressAutocomplete.addListener("place_changed", () => {
            const place = this.addressAutocomplete.getPlace();

            if (!place || !place.address_components) {
                return;
            }

            // Reset the current values to ensure no stale data remains
            this.addressComponentValues = createGoogleAddressComponentValueMap();

            // Map components directly
            place.address_components.forEach(addressComponent => {
                addressComponent.types.forEach(type => {
                    switch (type) {
                        case 'street_number':
                            this.addressComponentValues.street_number = addressComponent.long_name;
                            break;
                        case 'route':
                            this.addressComponentValues.route = addressComponent.long_name;
                            break;
                        case 'locality':
                            this.addressComponentValues.locality = addressComponent.long_name;
                            break;
                        case 'postal_town':
                            this.addressComponentValues.postal_town = addressComponent.long_name;
                            break;
                        case 'administrative_area_level_1':
                            this.addressComponentValues.administrative_area_level_1 = addressComponent.short_name;
                            break;
                        case 'administrative_area_level_2':
                            this.addressComponentValues.administrative_area_level_2 = addressComponent.short_name;
                            break;
                        case 'postal_code':
                            this.addressComponentValues.postal_code = addressComponent.long_name;
                            break;
                        case 'postal_code_prefix':
                            this.addressComponentValues.postal_code_prefix = addressComponent.long_name;
                            break;
                        case 'country':
                            this.addressComponentValues.country = addressComponent.short_name;
                            break;
                    }
                });
            });

            // Assign values using fallback logic
            const streetNumber = this.addressComponentValues.street_number || '';
            const route = this.addressComponentValues.route || '';
            this.creditCardData.billingStreet = [streetNumber, route].filter(Boolean).join(' ').trim();

            this.creditCardData.billingCity = this.addressComponentValues.locality
                || this.addressComponentValues.postal_town
                || this.addressComponentValues.administrative_area_level_2
                || '';

            this.creditCardData.billingState = this.addressComponentValues.administrative_area_level_1 || '';

            this.creditCardData.billingZip = this.addressComponentValues.postal_code
                || this.addressComponentValues.postal_code_prefix
                || '';
            this.creditCardData.billingCountry = `${this.addressComponentValues.country}`;
        });

        this.mapsIsInitialized = true;
    }

    createPaymentToken(autoPayOptions: AutoPayOption[], billablePartyId: number, selectedPaymentPlanId: number) {
        this.creditCardData.autoPayOptions = autoPayOptions;
        this.creditCardData.billablePartyId = billablePartyId;
        this.creditCardData.selectedPaymentPlanId = selectedPaymentPlanId;

        // If we are passing in new CC details then always use current payment processor
        this.creditCardData.paymentProcessorModule = noviNs.paymentProcessor;

        if (this.isPaymentProcessorQuickBookPayments) {
            let creditCardContainer = <QuickBooksPaymentsCardInputClass>this.$refs.quickBooksPaymentsCardInput;
            creditCardContainer.createQbPaymentToken(this.creditCardData);
        } else if (this.isPaymentProcessorFiserv) {
            let creditCardContainer = <FiservPaymentsCardInputClass>this.$refs.fiservPaymentsCardInput;
            creditCardContainer.createFiservPaymentToken(this.creditCardData);
        } else {
            this.emitError('Could not submit payment: No payment processor set up.');
        }
    }

    mounted() {
        const self = this;
        this.setupGoogleAutocomplete();

        // This is here as a fallback mechanism in case the google maps sdk has not been loaded when mounted() is executed
        window.addEventListener('load', () => {
            if (!this.mapsIsInitialized) {
                self.setupGoogleAutocomplete();
            }
        });
    }
}